import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Grid,
  Stack,
  Chip,
  IconButton,
  Autocomplete,
  CircularProgress,
  Paper,
  Collapse
} from '@mui/material';
import {
  QrCodeScanner,
  Delete,
  Person,
  Upload,
  ArrowBack,
  Send,
  ExpandMore,
  ExpandLess,
  Inventory
} from '@mui/icons-material';
import { DataService } from '../../services/data-service';
import { LovService } from '../../services/lov-service';
import { useAppDispatch } from 'src/redux/hooks';
import { showAlert } from 'src/redux/alert/alertReducer';
import ExcelImportDialog, { ImportRow } from '../../components/ExcelImportDialog';
import { BarcodeSearchResult, bulkBarcodeService } from '../../services/bulk-barcode-service';
import { useNavigate } from 'react-router-dom';
import { TransferOrderItem } from 'src/types/TransferOrder.types';

interface Client {
  id: string;
  name: string;
}

interface TransferOrderRow {
  id: string;
  barcode: string;
  searchResult: BarcodeSearchResult | null;
  expectedQuantity: number;
  loading: boolean;
  error?: string;
  expandedLocations?: boolean; // For showing/hiding location details
}

const CreateTransferOrder: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [clients, setClients] = useState<Client[]>([]);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [orderName, setOrderName] = useState('');
  const [notes, setNotes] = useState('');
  const [transferRows, setTransferRows] = useState<TransferOrderRow[]>([
    { id: '1', barcode: '', searchResult: null, expectedQuantity: 1, loading: false }
  ]);
  const [submitting, setSubmitting] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [loadingClients, setLoadingClients] = useState(false);

  useEffect(() => {
    loadClients();
  }, []);

  const loadClients = async () => {
    setLoadingClients(true);
    try {
      const clientsData = await LovService.getLovDictionary('all-clients');
      const clientsList: Client[] = Object.entries(clientsData || {}).map(([id, name]) => ({
        id,
        name: String(name)
      }));
      setClients(clientsList);
    } catch (error: any) {
      dispatch(showAlert({ message: error.message || 'Failed to load clients', type: 'warning' }));
      setClients([]);
    } finally {
      setLoadingClients(false);
    }
  };

  const searchBarcode = async (rowId: string, barcode: string) => {
    if (!barcode.trim()) return;

    const currentRow = transferRows.find(r => r.id === rowId);
    if (currentRow?.searchResult && currentRow.barcode === barcode) {
      return;
    }

    setTransferRows(prev => prev.map(row =>
      row.id === rowId
        ? { ...row, loading: true, error: undefined, searchResult: null }
        : row
    ));

    try {
      const response = await DataService.get(`api/inventoryItem/search-barcode/${encodeURIComponent(barcode)}`);

      if (!response.ok) {
        throw new Error('Failed to search barcode');
      }

      const result = await response.json();

      if (!result?.success) {
        throw new Error(result?.message || 'Barcode not found');
      }

      const normalized = normalizeSingleSearch(result.data);

      setTransferRows(prev => prev.map(row =>
        row.id === rowId
          ? {
              ...row,
              loading: false,
              barcode: barcode,
              searchResult: normalized,
              error: normalized ? undefined : `${result.data?.itemName || barcode} not found or not in inventory`,
              expandedLocations: false
            }
          : row
      ));

      if (!normalized) {
        dispatch(showAlert({
          message: result.data?.itemName
            ? `${result.data.itemName} is known but not in inventory.`
            : `${barcode} not found.`,
          type: 'warning'
        }));
      }
    } catch (error: any) {
      setTransferRows(prev => {
        const filtered = prev.filter(row => row.id !== rowId);
        if (filtered.length === 0 || !filtered.some(row => !row.barcode?.trim())) {
          filtered.push({
            id: `empty-${Date.now()}`,
            barcode: '',
            searchResult: null,
            expectedQuantity: 1,
            loading: false
          });
        }
        return filtered;
      });

      dispatch(showAlert({
        message: `${barcode}: ${error.message || 'Search failed'}. Row removed.`,
        type: 'error'
      }));
    }
  };

  const filterStockedLocations = <T extends { totalQuantity?: number }>(locs?: T[]) =>
    (locs || []).filter(l => (l.totalQuantity ?? 0) > 0);

  const normalizeSingleSearch = (data: any): any | null => {
    if (!data) return null;
    const locs = filterStockedLocations(data.locations);
    if (!locs.length) return null;
    return {
      ...data,
      locations: locs
    };
  };

  const handleBarcodeChange = (rowId: string, barcode: string) => {
    setTransferRows(prev => prev.map(row =>
      row.id === rowId
        ? {
            ...row,
            barcode,
            searchResult: null,
            error: undefined
          }
        : row
    ));
  };

  const handleBarcodeSubmit = (rowId: string) => {
    const row = transferRows.find(r => r.id === rowId);
    if (row && row.barcode.trim()) {
      searchBarcode(rowId, row.barcode);

      const currentIndex = transferRows.findIndex(r => r.id === rowId);
      if (currentIndex === transferRows.length - 1) {
        addNewRow();
      }
    }
  };

  const addNewRow = () => {
    const newId = Date.now().toString();
    setTransferRows(prev => [...prev, {
      id: newId,
      barcode: '',
      searchResult: null,
      expectedQuantity: 1,
      loading: false
    }]);
  };

  const removeRow = (rowId: string) => {
    if (transferRows.length === 1) return;
    setTransferRows(prev => prev.filter(row => row.id !== rowId));
  };

  const updateRowField = (rowId: string, field: keyof TransferOrderRow, value: any) => {
    setTransferRows(prev => prev.map(row =>
      row.id === rowId ? { ...row, [field]: value } : row
    ));
  };

  const toggleLocationExpand = (rowId: string) => {
    setTransferRows(prev => prev.map(row =>
      row.id === rowId ? { ...row, expandedLocations: !row.expandedLocations } : row
    ));
  };

  const statusCounts = useMemo(() => {
    const totalRows = transferRows.length;
    const withBarcodes = transferRows.filter(row => row.barcode.trim()).length;
    const readyToTransfer = transferRows.filter(row =>
      row.barcode.trim() && row.searchResult && (
        // Boxes are always ready (no quantity needed)
        row.searchResult.type === 'box' ||
        // Units need quantity > 0
        row.expectedQuantity > 0
      )
    ).length;
    const errors = transferRows.filter(row => row.error).length;
    const boxCount = transferRows.filter(row => row.searchResult?.type === 'box').length;
    const unitCount = transferRows.filter(row => row.searchResult?.type !== 'box' && row.searchResult).length;

    return {
      totalRows,
      withBarcodes,
      readyToTransfer,
      errors,
      boxCount,
      unitCount,
      completion: withBarcodes > 0 ? Math.round((readyToTransfer / withBarcodes) * 100) : 0
    };
  }, [transferRows]);

  const validateTransferOrder = (): string[] => {
    const errors: string[] = [];

    if (!selectedClient) {
      errors.push('Please select a client');
    }

    if(!orderName){
      errors.push('Please enter order name');
    }

    const allRows = transferRows.slice(0, -1).concat(
      transferRows.slice(-1).filter(row => row.barcode.trim())
    );
    const validRows = allRows.filter(row => row.barcode.trim());

    if (validRows.length === 0) {
      errors.push('At least one item must be scanned');
    }

    validRows.forEach((row, index) => {
      if (!row.searchResult) {
        errors.push(`Row ${index + 1}: Item not found in inventory`);
      } else if (row.searchResult.type !== 'box' && row.expectedQuantity <= 0) {
        // Only validate quantity for units (boxes are transferred as-is)
        errors.push(`Row ${index + 1}: Quantity must be greater than 0`);
      }
    });

    return errors;
  };

  const handleSubmit = async () => {
    const validationErrors = validateTransferOrder();
    if (validationErrors.length > 0) {
      dispatch(showAlert({ message: validationErrors.join('\n'), type: 'warning' }));
      return;
    }

    setSubmitting(true);
    try {
      const allRows = transferRows.slice(0, -1).concat(
        transferRows.slice(-1).filter(row => row.barcode.trim())
      );
      const validRows = allRows.filter(row => row.barcode.trim() && row.searchResult);

      // Separate items by type: boxes vs units
      const boxBarcodes: string[] = [];
      const items: TransferOrderItem[] = [];

      validRows.forEach(row => {
        if (row.searchResult?.type === 'box') {
          // For boxes, just send the barcode
          boxBarcodes.push(row.barcode);
        } else {
          // For units, send barcode and quantity
          items.push({
            barcode: row.barcode,
            requestedQuantity: row.expectedQuantity
          });
        }
      });

      const payload = {
        clientId: selectedClient!.id,
        name: orderName.trim() || undefined,
        items: items,
        boxBarcodes: boxBarcodes.length > 0 ? boxBarcodes : undefined,
        notes: notes.trim() || undefined
      };

      const response = await DataService.post('api/TransferOrder/create', payload);

      if (!response.ok) {
        const errorResult = await response.json();
        throw new Error(errorResult.message || 'Failed to create transfer order');
      }

      const result = await response.json();

      const totalCount = items.length + boxBarcodes.length;
      const itemsDesc = items.length > 0 ? `${items.length} item(s)` : '';
      const boxesDesc = boxBarcodes.length > 0 ? `${boxBarcodes.length} box(es)` : '';
      const description = [itemsDesc, boxesDesc].filter(Boolean).join(' and ');

      dispatch(showAlert({
        message: `Successfully created transfer order for ${selectedClient!.name} with ${description}`,
        type: 'success'
      }));

      navigate('/transfer-order');
    } catch (error: any) {
      dispatch(showAlert({ message: error.message || 'Failed to create transfer order', type: 'error' }));
    } finally {
      setSubmitting(false);
    }
  };

  const canSubmit = !!selectedClient && statusCounts.readyToTransfer > 0;

  // Excel import handler
  const handleImport = async (importRows: ImportRow[]) => {
    if (!importRows.length) return;

    try {
      const qtyByBarcode = new Map<string, number>();
      for (const row of importRows) {
        const b = (row.barcode || '').trim();
        if (!b) continue;
        const q = Number(row.quantity) || 0;
        qtyByBarcode.set(b, (qtyByBarcode.get(b) || 0) + q);
      }
      const barcodes = [...qtyByBarcode.keys()];
      if (barcodes.length === 0) return;

      const results = await bulkBarcodeService.searchBarcodes(barcodes, {
        includeLocations: true,
        includeBoxes: true,
      });

      const foundItems: TransferOrderRow[] = [];
      let ignoredUnknown = 0;
      let ignoredNoInventory = 0;

      let currentId = Date.now();

      for (const r of results) {
        const qty = qtyByBarcode.get(r.barcode) ?? 0;

        if (!r.found || !r.locations || r.locations.length === 0) {
          if (r.itemName) ignoredNoInventory++;
          else ignoredUnknown++;
          continue;
        }

        const searchResult: BarcodeSearchResult = {
          type: r.type === 'box' ? 'box' : 'unit',
          itemName: r.itemName || '',
          unitId: r.unitId,
          locations: r.locations,
          unit: r.unit
        };

        foundItems.push({
          id: (currentId++).toString(),
          barcode: r.barcode,
          searchResult,
          expectedQuantity: Math.max(1, qty || 1),
          loading: false,
          expandedLocations: false
        });
      }

      setTransferRows(prev => {
        const existing = prev.filter(row => row.barcode.trim());
        return [
          ...existing,
          ...foundItems,
          {
            id: (Date.now() + foundItems.length).toString(),
            barcode: '',
            searchResult: null,
            expectedQuantity: 1,
            loading: false,
          },
        ];
      });

      const importedCount = foundItems.length;
      const ignoredTotal = ignoredUnknown + ignoredNoInventory;
      const msg =
        importedCount > 0
          ? ignoredTotal > 0
            ? `Imported ${importedCount} item(s). ${ignoredTotal} unavailable item(s) ignored.`
            : `Imported ${importedCount} item(s).`
          : `No items imported. ${ignoredTotal} unavailable item(s) ignored.`;

      dispatch(showAlert({
        message: msg,
        type: importedCount > 0 ? 'success' : 'warning',
      }));

      setImportDialogOpen(false);
    } catch (error: any) {
      dispatch(showAlert({
        message: error.message || 'Failed to import items',
        type: 'error',
      }));
      setImportDialogOpen(false);
      throw error;
    }
  };

  const handleCancel = () => {
    navigate('/transfer-order');
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', bgcolor: 'background.default' }}>
      {/* Header */}
      <Paper sx={{ borderRadius: 0, borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack direction="row" alignItems="center" spacing={2}>
              <IconButton onClick={() => navigate('/transfer-order')} aria-label="ArrowBack">
                <ArrowBack />
              </IconButton>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                  Create Transfer Order
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Scan items and specify expected quantities for client transfer
                </Typography>
              </Box>
            </Stack>

            <Stack direction="row" spacing={2}>
              <Button
                variant="outlined"
                onClick={handleCancel}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="success"
                onClick={handleSubmit}
                disabled={submitting || !canSubmit}
                startIcon={submitting ? <CircularProgress size={20} /> : <Send />}
              >
                {submitting ? 'Creating...' : 'Create Transfer Order'}
              </Button>
            </Stack>
          </Stack>
        </Box>

        {/* Client Selection */}
        <Box sx={{ p: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <Autocomplete<Client, false, false, false>
                options={clients}
                getOptionLabel={(option) => option.name}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Person fontSize="small" />
                      <Typography variant="body2">{option.name}</Typography>
                    </Stack>
                  </Box>
                )}
                value={selectedClient}
                onChange={(_, newValue) => setSelectedClient(newValue)}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Client *"
                    placeholder="Choose client..."
                    disabled={submitting || loadingClients}
                    required
                    error={!selectedClient}
                    size="small"
                  />
                )}
                loading={loadingClients}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Order Name"
                value={orderName}
                onChange={(e) => setOrderName(e.target.value)}
                placeholder="e.g., Q4-Warehouse-Transfer-001"
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Additional notes"
                fullWidth
                size="small"
              />
            </Grid>
          </Grid>
        </Box>
      </Paper>

      {/* Content */}
      <Box sx={{ flex: 1, p: 3 }}>
        <Stack spacing={2}>
          {/* Action Buttons */}
          <Card variant="outlined">
            <CardContent sx={{ p: 1.5 }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between">
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                    Actions:
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => setImportDialogOpen(true)}
                    startIcon={<Upload />}
                    disabled={submitting}
                  >
                    Import Excel
                  </Button>
                </Stack>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  {statusCounts.boxCount > 0 && (
                    <Chip
                      label={`${statusCounts.boxCount} Box${statusCounts.boxCount > 1 ? 'es' : ''}`}
                      size="small"
                      color="secondary"
                      variant="outlined"
                    />
                  )}
                  {statusCounts.unitCount > 0 && (
                    <Chip
                      label={`${statusCounts.unitCount} Unit${statusCounts.unitCount > 1 ? 's' : ''}`}
                      size="small"
                      color="default"
                      variant="outlined"
                    />
                  )}
                  <Chip
                    label={`${statusCounts.readyToTransfer} / ${statusCounts.withBarcodes} Ready`}
                    size="small"
                    color={statusCounts.completion === 100 ? "success" : "default"}
                    variant="outlined"
                  />
                  <Chip
                    label={`${statusCounts.completion}% Complete`}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                </Stack>
              </Stack>
            </CardContent>
          </Card>

          {/* Items Table */}
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Transfer Items</Typography>
            <Stack spacing={2}>
              {transferRows.map((row, index) => (
                <Card key={row.id} variant="outlined">
                  <CardContent sx={{ p: 1.5 }}>
                    <Stack direction="row" spacing={1.5} alignItems="flex-start">
                      {/* Barcode Input */}
                      <Box sx={{ flex: '0 0 200px' }}>
                        <TextField
                          value={row.barcode || ''}
                          onChange={(e) => handleBarcodeChange(row.id, e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleBarcodeSubmit(row.id);
                          }}
                          onBlur={() => handleBarcodeSubmit(row.id)}
                          placeholder="Scan barcode"
                          size="small"
                          fullWidth
                          disabled={submitting}
                          autoComplete="off"
                          InputProps={{
                            startAdornment: <QrCodeScanner sx={{ mr: 0.5, color: 'action.active', fontSize: '1.2rem' }} />,
                            endAdornment: row.loading && <CircularProgress size={16} />
                          }}
                          error={!!row.error}
                        />
                      </Box>

                      {/* Item Details & Location Chips */}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        {row.searchResult ? (
                          <Stack spacing={0.5}>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Typography variant="body2" fontWeight="medium" noWrap>
                                {row.searchResult.itemName || row.barcode}
                              </Typography>
                              <Chip
                                label={row.searchResult.type === 'box' ? 'Box' : 'Unit'}
                                size="small"
                                variant="outlined"
                                color={row.searchResult.type === 'box' ? 'secondary' : 'default'}
                                sx={{ height: 20, fontSize: '0.7rem' }}
                              />
                              {row.searchResult.type === 'box' && (
                                <Chip
                                  label="Auto-included"
                                  size="small"
                                  variant="filled"
                                  color="info"
                                  sx={{ height: 20, fontSize: '0.65rem' }}
                                />
                              )}
                            </Stack>
                            {/* Location chips in same row */}
                            {row.searchResult.locations && row.searchResult.locations.length > 0 && (
                              <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ gap: 0.5 }}>
                                {row.searchResult.locations.slice(0, 3).map((location: any, locIndex: number) => (
                                  <Chip
                                    key={locIndex}
                                    label={`${location.storageUnitName || location.storageUnitCode}: ${location.availableQuantity || location.totalQuantity}`}
                                    size="small"
                                    variant="outlined"
                                    color="primary"
                                    sx={{ height: 20, fontSize: '0.7rem' }}
                                    onClick={() => toggleLocationExpand(row.id)}
                                  />
                                ))}
                                {row.searchResult.locations.length > 3 && (
                                  <Chip
                                    label={`+${row.searchResult.locations.length - 3} more`}
                                    size="small"
                                    variant="outlined"
                                    color="primary"
                                    sx={{ height: 20, fontSize: '0.7rem' }}
                                    onClick={() => toggleLocationExpand(row.id)}
                                  />
                                )}
                              </Stack>
                            )}
                          </Stack>
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            {row.barcode ? 'Searching...' : 'Enter barcode'}
                          </Typography>
                        )}
                        {row.error && (
                          <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                            {row.error}
                          </Typography>
                        )}

                        {/* Expandable Location Details */}
                        {row.searchResult && row.searchResult.locations && (
                          <Collapse in={row.expandedLocations}>
                            <Box sx={{ mt: 1, p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
                              <Typography variant="caption" fontWeight="medium" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                                <Inventory sx={{ fontSize: '0.9rem' }} />
                                Available Locations
                              </Typography>
                              <Stack spacing={0.5}>
                                {row.searchResult.locations.map((location: any, locIndex: number) => (
                                  <Box key={locIndex}>
                                    <Typography variant="caption" fontWeight="medium">
                                      {location.storageUnitName || location.storageUnitCode}:
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                      {location.independentQuantity > 0 && `Ind: ${location.independentQuantity}`}
                                      {location.boxes && location.boxes.length > 0 && `, Boxes: ${location.boxes.length}`}
                                      {' | Total: '}
                                      <strong>{location.availableQuantity || location.totalQuantity}</strong>
                                    </Typography>
                                  </Box>
                                ))}
                              </Stack>
                            </Box>
                          </Collapse>
                        )}
                      </Box>

                      {/* Quantity */}
                      <Box sx={{ flex: '0 0 100px' }}>
                        <TextField
                          type="number"
                          label="Qty"
                          value={row.searchResult?.type === 'box' ? 'N/A' : row.expectedQuantity}
                          onChange={(e) => {
                            const newQuantity = parseInt(e.target.value) || 1;
                            updateRowField(row.id, 'expectedQuantity', Math.max(1, newQuantity));
                          }}
                          size="small"
                          fullWidth
                          inputProps={{ min: 1 }}
                          disabled={submitting || !row.searchResult || row.searchResult?.type === 'box'}
                          helperText={row.searchResult?.type === 'box' ? 'Full box' : ''}
                        />
                      </Box>

                      {/* Actions */}
                      <Stack direction="row" spacing={0.5}>
                        {row.searchResult && row.searchResult.locations && row.searchResult.locations.length > 0 && (
                          <IconButton
                            size="small"
                            onClick={() => toggleLocationExpand(row.id)}
                            color="primary"
                          >
                            {row.expandedLocations ? <ExpandLess /> : <ExpandMore />}
                          </IconButton>
                        )}
                        {transferRows.length > 1 && (
                          <IconButton
                            size="small"
                            onClick={() => removeRow(row.id)}
                            disabled={submitting}
                            color="error"
                          >
                            <Delete />
                          </IconButton>
                        )}
                      </Stack>
                    </Stack>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          </Paper>
        </Stack>
      </Box>

      {/* Excel Import Dialog */}
      <ExcelImportDialog
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        onImport={handleImport}
        title="Import Items for Transfer Order"
        expectedColumns={{
          required: ['barcode', 'quantity'],
          optional: []
        }}
      />
    </Box>
  );
};

export default CreateTransferOrder;
